import { graphqlPost } from "api/graphqlClient";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { CustomObjectProvisioningState } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { DashboardRecordLink } from "../DashboardRecordLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { RecordListWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";
import {
    clampPageOffset,
    computeAdaptiveRowCount,
    MAX_ADAPTIVE_ROWS,
    measuredAvailableHeight,
    parseRecordListResponse,
} from "./recordListWidgetFit";
import {
    computeStripPagerRange,
    nextPageOffset,
    prevPageOffset,
} from "./statusStripPager";

interface StripRecord {
    id: string;
    name: string;
    provisioning_state: CustomObjectProvisioningState;
}

const POLL_INTERVAL_MS = 10000;
/** Debounce for the resize-driven page-size refit, in ms. */
const RESIZE_REFIT_DEBOUNCE_MS = 150;
/** Page size used only before the panel height has been measured, or when it
 *  cannot be measured (widget rendered detached / at zero height). */
const DEFAULT_PAGE_SIZE = 10;

/**
 * Rendered pitch of one strip row, in px, used to derive the adaptive PAGE SIZE
 * from the available panel height. A row is one `.DashboardStatusStrip__row`
 * (a provisioning badge + the record-name link on a single line) plus the 6px
 * flex-column `gap` to the next row (see `.DashboardStatusStrip` in
 * DashboardView.scss). Measured live on develop at ~24px content + 6px gap.
 * Deriving the page size from this CONSTANT (not a data-dependent measurement)
 * means the widget issues exactly ONE list request per page view — no
 * measure→refetch loop. Keep in lock-step with the strip row content height +
 * the `gap` in DashboardView.scss; tune live via fe-verify on a height-varied
 * dashboard.
 */
const STRIP_ROW_PITCH_PX = 30;
/**
 * Fallback height (px) of the pager footer shown BELOW the rows, subtracted from
 * the usable height so the footer never pushes the last row past the panel into
 * an internal scrollbar. The live height is measured from the DOM when available
 * (see {@link measureGeometry}); this constant is only used before the footer
 * has painted.
 */
const PAGER_FOOTER_PX = 34;

/**
 * `status_strip` widget — a compact column of custom objects with their
 * provisioning state shown as a coloured badge. Designed for at-a-glance health
 * of a set of infrastructure objects (buildservers, installations, etc.).
 *
 * Only meaningful for CUSTOM_OBJECT records (provisioning_state comes from there).
 *
 * LIMIT BEHAVIOUR (aligns with the record_list server-side lazy-pagination
 * standard, operator-task-fe-list-widgets-serverside-lazy-pagination-standard):
 * the widget ALWAYS uses height-adaptive server-side pagination. The page size
 * is derived from the space actually available (how many rows fit the panel
 * height, capped at {@link MAX_ADAPTIVE_ROWS}); the true total row count is
 * shown; and switching pages fetches only that page. The widget therefore holds
 * one visible page in memory rather than a whole `limit`-sized batch — which is
 * what previously drove the dashboard full-load / high-CPU "fan-whirr" symptom.
 * `config.limit` is NOT a page-size knob for status_strip (widget HEIGHT is the
 * single source of the page size); it is ignored here, kept only for stored-config
 * back-compat.
 */
export const StatusStripWidget = ({ config }: WidgetComponentProps<RecordListWidgetConfig>) => {
    const [records, setRecords] = useState<StripRecord[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    // The last records committed to state — used to skip a no-op re-render when a
    // `live` re-poll returns identical data (see fetchRecords).
    const prevDataRef = useRef<StripRecord[] | undefined>(undefined);
    const fitContainerRef = useRef<HTMLDivElement | null>(null);

    const order = config?.order ?? null;
    const live = config?.live ?? false;
    const filter =
        config?.filter == null
            ? null
            : typeof config.filter === "string"
            ? config.filter
            : JSON.stringify(config.filter);

    // Adaptive PAGE SIZE: how many whole rows fit the panel height, derived from a
    // row-pitch CONSTANT (not a data-dependent measurement). `null` until the panel
    // has been measured → fetchRecords defers so the first fetch is already sized
    // to the panel (one fetch, no bootstrap-then-refetch).
    const [pageSize, setPageSize] = useState<number | null>(null);
    // Current page start (server-side pagination). Reset to 0 when the page size,
    // sort, or the widget's data selection changes.
    const [offset, setOffset] = useState(0);
    // Server total row count — drives the pager range + "of N" label.
    const [totalCount, setTotalCount] = useState(0);
    const limit = pageSize;

    const fetchRecords = useCallback(async (): Promise<boolean> => {
        // Defer the request until the panel has been measured (pageSize set →
        // limit non-null), so the very first fetch is already sized to the panel.
        if (limit == null) {
            return false;
        }
        // Real server-side pagination via the `custom_object_list` wrapper
        // (`{ custom_object_count, custom_object_list }`, accepts `offset`), so the
        // pager can step through the whole set one page per request.
        const query = `query DashboardStatusStrip($limit: Int, $order: String, $filter: String, $offset: Int) {
            custom_object_list(limit: $limit, order: $order, filter: $filter, offset: $offset) {
                custom_object_count
                custom_object_list {
                    id
                    name
                    provisioning_state
                }
            }
        }`;
        try {
            const response = await graphqlPost({
                query,
                variables: { limit, order, filter, offset },
            });
            const { rows, totalCount: nextCount } = parseRecordListResponse(
                response?.data,
                "custom_object",
                true,
            );
            const next = rows as StripRecord[];
            // Skip the re-render (and the lastUpdatedAt bump) when a `live`
            // re-poll returns identical data.
            const changed = !isEqual(prevDataRef.current, next);
            if (isMounted.current) {
                if (changed) {
                    prevDataRef.current = next;
                    setRecords(next);
                }
                // Keep the server total current even when the current page's rows
                // do not change (e.g. a record added on a later page).
                if (nextCount != null) {
                    setTotalCount(nextCount);
                }
                setError(null);
                setLoading(false);
            }
            return changed;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load."));
                setLoading(false);
            }
            return false;
        }
    }, [limit, order, filter, offset]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchRecords,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchRecords]);

    // Compute the panel's available height from ONLY data-independent inputs, so it
    // never depends on how many rows are rendered (the coupling that would create a
    // measure→refetch loop): the nearest BOUNDED scroll-clipping ancestor's
    // clientHeight (measuredAvailableHeight) — NOT the `height: 100%` wrapper's own
    // rect, which grows to its CONTENT height when the percentage chain fails to
    // resolve. The strip has no header, so topChrome is 0; the bottom chrome is the
    // pager footer height (measured live, constant fallback before it paints).
    const measureGeometry = useCallback(() => {
        const container = fitContainerRef.current;
        if (!container) {
            return null;
        }
        const availableHeight = measuredAvailableHeight(container);
        const pager = container.querySelector(
            ".DashboardStatusStrip__pager",
        ) as HTMLElement | null;
        const bottomChrome =
            pager && pager.getBoundingClientRect().height > 0
                ? pager.getBoundingClientRect().height
                : PAGER_FOOTER_PX;
        return { availableHeight, bottomChrome };
    }, []);

    // Data-INDEPENDENT: derive the PAGE SIZE (rows per page) from the panel height
    // and the row-pitch constant. Because it never depends on the rendered rows, it
    // gates exactly ONE fetch of `pageSize` rows per page view. A changed page size
    // re-pages from the top (offset reset in the same batched update).
    const pageSizeRef = useRef<number | null>(null);
    const measurePageSize = useCallback(() => {
        const geo = measureGeometry();
        if (!geo) {
            return;
        }
        const fit = computeAdaptiveRowCount(
            geo.availableHeight,
            0,
            STRIP_ROW_PITCH_PX,
            MAX_ADAPTIVE_ROWS,
            geo.bottomChrome,
        );
        if (fit == null || pageSizeRef.current === fit) {
            return;
        }
        pageSizeRef.current = fit;
        setPageSize(fit);
        setOffset(0);
    }, [measureGeometry]);

    // Re-derive the page size when the widget resizes (debounced).
    useEffect(() => {
        const container = fitContainerRef.current;
        if (!container || typeof ResizeObserver === "undefined") {
            return;
        }
        let timer: ReturnType<typeof setTimeout> | null = null;
        const observer = new ResizeObserver(() => {
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                measurePageSize();
            }, RESIZE_REFIT_DEBOUNCE_MS);
        });
        observer.observe(container);
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, [measurePageSize]);

    // Measure the page size once the empty strip chrome paints. A layout effect
    // runs before paint, so the gated first fetch fires immediately at the right
    // size. Deliberately NOT keyed on `records`.
    useLayoutEffect(() => {
        measurePageSize();
    }, [measurePageSize]);

    // The widget's data selection (filter / order) changed → page back to the top.
    useEffect(() => {
        setOffset(0);
    }, [filter, order]);

    // Clamp the offset back into range when the server total shrinks below the
    // current page (e.g. records deleted while a later page was shown), so the
    // pager never sits on an empty page.
    useEffect(() => {
        if (pageSize == null) {
            return;
        }
        const clamped = clampPageOffset(offset, pageSize, totalCount);
        if (clamped !== offset) {
            setOffset(clamped);
        }
    }, [offset, pageSize, totalCount]);

    // Defensive fallback: if the panel can't be measured (rendered detached / at
    // zero height), still issue a fetch at the default page size so it never hangs
    // on "loading". The normal path sets pageSize via measurePageSize first.
    useEffect(() => {
        if (pageSize != null) {
            return;
        }
        const timer = setTimeout(() => {
            setPageSize((prev) => {
                if (prev != null) {
                    return prev;
                }
                pageSizeRef.current = DEFAULT_PAGE_SIZE;
                return DEFAULT_PAGE_SIZE;
            });
        }, 1200);
        return () => clearTimeout(timer);
    }, [pageSize]);

    const goPrev = useCallback(() => {
        setOffset((prev) => prevPageOffset(prev, pageSizeRef.current ?? DEFAULT_PAGE_SIZE));
    }, []);
    const goNext = useCallback(() => {
        setOffset((prev) => nextPageOffset(prev, pageSizeRef.current ?? DEFAULT_PAGE_SIZE));
    }, []);

    // Pager range labels (1-based, inclusive) + control state. `rangeEnd` is
    // bounded by the real page contents, so a short last page reports its true
    // last row. Pure math lives in statusStripPager (unit-tested).
    const { rangeStart, rangeEnd, hasPrev, hasNext, multiPage } =
        computeStripPagerRange({
            offset,
            recordCount: records.length,
            totalCount,
        });

    const strip = (
        <ul className="DashboardStatusStrip__list">
            {records.map((rec) => (
                <li key={rec.id} className="DashboardStatusStrip__row">
                    <DashboardRecordLink
                        id={rec.id}
                        name={rec.name}
                        recordType="custom_object"
                        provisioningState={rec.provisioning_state}
                    />
                </li>
            ))}
        </ul>
    );

    // Count + pager footer. The total count is shown whenever rows are loaded
    // (Margot's ask); the prev/next controls appear only when there is more than
    // one page. Chevron buttons use the shared Button `clear`/`small` variant per
    // the ratified -buttons design principle (inert-looking, icon-only nav).
    const footer =
        !isLoading && records.length > 0 ? (
            <div className="DashboardStatusStrip__pager">
                <span className="DashboardStatusStrip__count">
                    {rangeStart}–{rangeEnd} of {totalCount}
                </span>
                {multiPage ? (
                    <span className="DashboardStatusStrip__pagerControls">
                        <Button
                            variation="clear"
                            size="small"
                            icon={IconCollection.chevronLeft}
                            ariaLabel="Previous page"
                            labelOnlyAria
                            title="Previous page"
                            disabled={!hasPrev}
                            onClick={goPrev}
                        />
                        <Button
                            variation="clear"
                            size="small"
                            icon={IconCollection.chevronRight}
                            ariaLabel="Next page"
                            labelOnlyAria
                            title="Next page"
                            disabled={!hasNext}
                            onClick={goNext}
                        />
                    </span>
                ) : null}
            </div>
        ) : null;

    let content;
    // Keep the last strip on a failed re-fetch; surface the error inline on the
    // live bar instead of blanking. Otherwise always render the measuring
    // container (so the panel geometry can be read and the single gated fetch can
    // fire), showing the empty/loading state inside it.
    if (error && records.length === 0) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else {
        let inner;
        if (isLoading && records.length === 0) {
            inner = <div className="DashboardWidget__empty">Loading…</div>;
        } else if (records.length === 0 && pageSize != null) {
            inner = <div className="DashboardWidget__empty">No records.</div>;
        } else {
            inner = strip;
        }
        content = (
            <div ref={fitContainerRef} className="DashboardStatusStrip">
                <div className="DashboardStatusStrip__body">{inner}</div>
                {footer}
            </div>
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
